import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const formatTime = value => new Intl.DateTimeFormat('nl-NL', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
const participantKey = row => row.invite_id || `${row.student_id}:${row.parent_id}`;

export default function AdminMeetingBooking({ roundId, booking, onClose, onSaved }) {
  const [invites, setInvites] = useState([]);
  const [inviteId, setInviteId] = useState('');
  const [details, setDetails] = useState(null);
  const [slotId, setSlotId] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const closeRef = useRef(null);
  const token = sessionStorage.getItem('jwtToken');
  const selected = invites.find(row => participantKey(row) === inviteId);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    let active = true;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/meeting-round/admin/${roundId}/invites`, { headers: { Authorization: `Bearer ${token}` } })
      .then(response => {
        if (!active) return;
        const rows = response.data.data || [];
        setInvites(rows);
        const target = booking && rows.find(row => row.student_id === booking.student_id && row.parent_id === booking.parent_id);
        if (target) setInviteId(participantKey(target));
      })
      .catch(() => { if (active) setError('Deelnemers konden niet worden geladen. Sluit dit venster en probeer opnieuw.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; document.body.style.overflow = overflow; previousFocus?.focus(); };
  }, [roundId, booking, token]);

  useEffect(() => {
    let active = true;
    setDetails(null); setSlotId('');
    if (!selected) { setLoadingSlots(false); return; }
    setLoadingSlots(true); setError('');
    const path = selected.invite_id ? `/invite/${selected.invite_id}/slots` : `/admin/${roundId}/slots`;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/meeting-round${path}`, {
      headers: { Authorization: `Bearer ${token}` },
      ...(!selected.invite_id ? { params: { student_id: selected.student_id, parent_id: selected.parent_id } } : {})
    })
      .then(response => { if (active) setDetails(response.data.data); })
      .catch(() => { if (active) setError('Tijdsloten konden niet worden geladen. Kies de deelnemer opnieuw.'); })
      .finally(() => { if (active) setLoadingSlots(false); });
    return () => { active = false; };
  }, [selected, roundId, token]);

  const existing = details?.existing_booking;
  const pastBooking = existing && new Date(existing.start_at) <= new Date();
  const save = async event => {
    event.preventDefault();
    if (!selected || !slotId || saving || pastBooking) return;
    setSaving(true); setError('');
    try {
      const path = selected.invite_id ? `/invite/${selected.invite_id}` : `/admin/${roundId}`;
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/meeting-round${path}/${existing ? 'rebook' : 'book'}`, {
        slot_id: slotId, ...(!selected.invite_id ? { student_id: selected.student_id, parent_id: selected.parent_id } : {})
      }, { headers: { Authorization: `Bearer ${token}` } });
      await onSaved();
      onClose();
    } catch (err) { setError(err.response?.data?.error || 'Opslaan mislukt. Probeer opnieuw.'); }
    finally { setSaving(false); }
  };

  return <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.45)', zIndex: 10000 }} role="dialog" aria-modal="true" aria-labelledby="admin-meeting-title"
    onKeyDown={event => {
      if (event.key === 'Escape' && !saving) { event.preventDefault(); onClose(); }
      if (event.key === 'Tab') {
        const controls = [...event.currentTarget.querySelectorAll('button:not(:disabled),select:not(:disabled)')];
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }}>
    <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable"><div className="modal-content">
      <div className="modal-header"><h3 id="admin-meeting-title" className="modal-title h5">{booking ? 'Oudergesprek wijzigen' : 'Ouder inschrijven'}</h3>
        <button ref={closeRef} className="btn-close" aria-label="Sluiten" onClick={onClose} disabled={saving} /></div>
      <form onSubmit={save} style={{ minWidth: 0, overflowY: 'auto' }}>
        <div className="modal-body">
          <p className="small text-muted">Als beheerder kun je ook na de sluitdatum een toekomstig gesprek inplannen of verplaatsen, ook voor een ouder en leerling zonder eerdere uitnodiging.</p>
          {error && <div className="alert alert-danger" role="alert">{error}</div>}
          {loading ? <p>Deelnemers laden...</p> : <>
            <label htmlFor="admin-meeting-parent" className="form-label">Ouder en leerling</label>
            <select id="admin-meeting-parent" className="form-select mb-3" value={inviteId} onChange={event => setInviteId(event.target.value)} disabled={saving} required>
              <option value="">Kies een ouder en leerling</option>
              {invites.map(invite => <option key={participantKey(invite)} value={participantKey(invite)}>{invite.student_name} — {invite.parent_name}{invite.booking_id ? ' (ingepland)' : !invite.invite_id ? ' (nog niet uitgenodigd)' : ''}</option>)}
            </select>
            {invites.length === 0 && <div className="alert alert-info">Er zijn geen deelnemers beschikbaar om in te schrijven.</div>}
            {loadingSlots && inviteId && <p>Tijdsloten laden...</p>}
            {details && <>
              {existing && <div className="alert alert-info">Huidige afspraak: {formatTime(existing.start_at)}</div>}
              {pastBooking ? <div className="alert alert-warning">Dit gesprek is al begonnen of voorbij en kan niet meer worden verplaatst.</div> : <>
                <label htmlFor="admin-meeting-slot" className="form-label">Beschikbaar tijdslot</label>
                <select id="admin-meeting-slot" className="form-select" value={slotId} onChange={event => setSlotId(event.target.value)} disabled={saving} required>
                  <option value="">Kies een tijdslot</option>
                  {(details.slots || []).map(slot => <option key={slot.slot_id} value={slot.slot_id}>{formatTime(slot.start_at)} – {new Date(slot.end_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}</option>)}
                </select>
                {!details.slots?.length && <p className="text-muted mt-2">Er zijn geen toekomstige vrije tijdsloten beschikbaar.</p>}
              </>}
            </>}
          </>}
        </div>
        <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Annuleren</button>
          <button className="btn btn-success" disabled={loading || loadingSlots || saving || !slotId || pastBooking}>{saving ? 'Opslaan...' : existing ? 'Afspraak wijzigen' : 'Inschrijven'}</button></div>
      </form>
    </div></div>
  </div>;
}
